'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import UIFlex from 'UIComponents/layout/UIFlex';
import EmailForm from 'MeetingsLib/components/sections/EmailForm';
import DatePickerSection from 'MeetingsLib/components/sections/DatePickerSection';
import TimeOptionsSection from 'MeetingsLib/components/sections/TimeOptionsSection';
import PropertyForm from 'MeetingsLib/components/sections/PropertyForm';
import PropertyFormV2 from 'MeetingsLib/components/sections/PropertyFormV2';
import UIStickyFooter from 'UIComponents/panel/UIStickyFooter';
import classNames from 'classnames';
import FreemiumBookingUpgradeLink from 'MeetingsLib/components/freemium/FreemiumBookingUpgradeLink';
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import BookingLifecycle from 'MeetingsLib/models/BookingLifecycle';
import FormState from 'MeetingsLib/models/FormState';
import WelcomeScreenInfo from 'MeetingsBase/models/WelcomeScreenInfo';
import { getPaidMeetingsAvailableSelector, getPaymentLinkInfo, getIsUngatedForAddGuests } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getRecaptchaEnabled } from 'MeetingsLib/selectors/BookInfoSelectors';
import PaymentForm from '../payments/PaymentIframe';
import ErrorBoundary from '../ErrorBoundary';

class CompactBookingPage extends Component {
  constructor(props) {
    super(props);

    this.renderEmailFormStep = () => {
      const {
        branding,
        welcomeScreenInfo,
        companyAvatar,
        meetingChangeId,
        EmailFormMarker
      } = this.props;
      return /*#__PURE__*/_jsx(EmailForm, {
        branding: branding,
        compact: true,
        logoUrl: welcomeScreenInfo.logoUrl,
        meetingChangeId: meetingChangeId,
        companyAvatar: companyAvatar,
        title: welcomeScreenInfo.title,
        useCompanyLogo: welcomeScreenInfo.useCompanyLogo,
        description: welcomeScreenInfo.description,
        EmailFormMarker: EmailFormMarker
      });
    };

    this.renderDatePickerStep = () => {
      const {
        avatar,
        companyAvatar,
        branding,
        displayAvatarOption,
        embedded,
        headline,
        profile,
        DatePickerMarker,
        TimePickerMarker
      } = this.props;
      return /*#__PURE__*/_jsx(DatePickerSection, {
        avatar: avatar,
        companyAvatar: companyAvatar,
        branding: branding,
        displayAvatarOption: displayAvatarOption,
        compact: true,
        embedded: embedded,
        headline: headline,
        profile: profile,
        DatePickerMarker: DatePickerMarker,
        TimePickerMarker: TimePickerMarker
      }, "compact-date");
    };

    this.renderTimeOptionsStep = () => {
      const {
        branding,
        profile,
        isFetchingPublicAvailability,
        resetEventDate,
        TimePickerMarker
      } = this.props;
      return /*#__PURE__*/_jsx(TimeOptionsSection, {
        branding: branding,
        compact: true,
        goToPreviousStep: resetEventDate,
        isFetchingPublicAvailability: isFetchingPublicAvailability,
        profile: profile,
        TimePickerMarker: TimePickerMarker
      }, "compact-time");
    };

    this.renderPropertyFormStep = () => {
      const {
        branding,
        embedded,
        canSubmitInitialPropertyForm,
        submittingMeeting,
        resetEventDateTime,
        isUngatedForAddGuests
      } = this.props;

      if (isUngatedForAddGuests) {
        return /*#__PURE__*/_jsx(PropertyFormV2, {
          branding: branding,
          canSubmitInitialPropertyForm: canSubmitInitialPropertyForm,
          goToPreviousStep: resetEventDateTime,
          submittingMeeting: submittingMeeting,
          compact: true
        }, "compact-property");
      }

      return /*#__PURE__*/_jsx(PropertyForm, {
        branding: branding,
        canSubmitInitialPropertyForm: canSubmitInitialPropertyForm,
        goToPreviousStep: resetEventDateTime,
        compact: true,
        embedded: embedded,
        submittingMeeting: submittingMeeting
      }, "compact-property");
    };

    this.renderPaymentForm = () => {
      const {
        resetFormState,
        resetConsentResponses,
        branding,
        submittingMeeting,
        recaptchaEnabled
      } = this.props;
      return /*#__PURE__*/_jsx(ErrorBoundary, {
        children: /*#__PURE__*/_jsx(PaymentForm, {
          goToPreviousStep: () => {
            resetFormState();
            resetConsentResponses();
          },
          branding: branding,
          submittingMeeting: submittingMeeting,
          recaptchaEnabled: recaptchaEnabled
        }, "payment-form")
      });
    };

    this.renderCurrentStep = () => {
      const {
        shouldShowEmailForm,
        bookingLifecycle,
        emailFormState
      } = this.props;

      if (shouldShowEmailForm && !emailFormState) {
        return this.renderEmailFormStep();
      }

      if (!bookingLifecycle.event.dateString) {
        return this.renderDatePickerStep();
      }

      if (!bookingLifecycle.event.dateTime) {
        return this.renderTimeOptionsStep();
      }

      return this.renderPropertyFormStep();
    };

    this.renderCurrentStepWithPayments = () => {
      const {
        shouldShowEmailForm,
        bookingLifecycle,
        emailFormState,
        formState,
        legalConsentValid
      } = this.props;

      if (shouldShowEmailForm && !emailFormState) {
        return this.renderEmailFormStep();
      }

      if (!bookingLifecycle.event.dateString) {
        return this.renderDatePickerStep();
      }

      if (!bookingLifecycle.event.dateTime) {
        return this.renderTimeOptionsStep();
      }

      if (!formState || !legalConsentValid) {
        return this.renderPropertyFormStep();
      }

      return this.renderPaymentForm();
    };

    this.state = {
      doTransition: false
    };
  }

  componentDidUpdate(prevProps, __prevState) {
    const selectedDate = this.props.bookingLifecycle.event.dateString;
    const selectedTime = this.props.bookingLifecycle.event.dateTime;
    const wasOnDatePickerStep = !prevProps.bookingLifecycle.event.dateString && selectedDate;
    const wasOnTimeOptionsSection = prevProps.bookingLifecycle.event.dateString && !selectedDate;
    const isOnTimeOptionsSection = wasOnDatePickerStep && !selectedTime;

    if ((prevProps.currentStep !== this.props.currentStep || isOnTimeOptionsSection || wasOnTimeOptionsSection) && !this.state.doTransition) {
      this.setState({
        doTransition: true
      });
    }
  }

  render() {
    const {
      embedded,
      isSalesStarter,
      bookingLifecycle,
      isPaidMeeting
    } = this.props;
    const containerClasses = classNames('compact-container m-x-auto width-100 min-height-0 p-top-4', !embedded && 'height-100', !isSalesStarter && 'm-bottom-12');
    const hasPickedDateTime = !!bookingLifecycle.event.dateTime;
    return /*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      className: containerClasses,
      children: [/*#__PURE__*/_jsx(CSSTransition, {
        classNames: "step-animation",
        timeout: 500,
        in: this.state.doTransition,
        onEntered: () => {
          this.setState({
            doTransition: false
          });
        },
        exit: false,
        children: /*#__PURE__*/_jsx("div", {
          className: "flex-column width-100 min-height-0 height-100",
          children: isPaidMeeting ? this.renderCurrentStepWithPayments() : this.renderCurrentStep()
        })
      }), !isSalesStarter && !hasPickedDateTime && /*#__PURE__*/_jsx(UIStickyFooter, {
        children: /*#__PURE__*/_jsx(FreemiumBookingUpgradeLink, {})
      })]
    });
  }

}

CompactBookingPage.propTypes = {
  avatar: PropTypes.string,
  companyAvatar: PropTypes.string,
  branding: PropTypes.instanceOf(Branding).isRequired,
  displayAvatarOption: PropTypes.string,
  canSubmitInitialPropertyForm: PropTypes.bool,
  currentStep: PropTypes.string,
  embedded: PropTypes.bool.isRequired,
  headline: PropTypes.string,
  isFetchingPublicAvailability: PropTypes.bool.isRequired,
  isSalesStarter: PropTypes.bool.isRequired,
  isRescheduling: PropTypes.bool,
  isPaidMeeting: PropTypes.bool,
  resetFormState: PropTypes.func.isRequired,
  resetConsentResponses: PropTypes.func.isRequired,
  recaptchaEnabled: PropTypes.bool.isRequired,
  meetingChangeId: PropTypes.string,
  profile: PropTypes.instanceOf(UserProfile),
  submittingMeeting: PropTypes.bool,
  shouldShowEmailForm: PropTypes.bool.isRequired,
  bookingLifecycle: PropTypes.instanceOf(BookingLifecycle).isRequired,
  emailFormState: PropTypes.string,
  formState: PropTypes.instanceOf(FormState),
  legalConsentValid: PropTypes.bool,
  resetEventDate: PropTypes.func.isRequired,
  resetEventDateTime: PropTypes.func.isRequired,
  welcomeScreenInfo: PropTypes.instanceOf(WelcomeScreenInfo).isRequired,
  DatePickerMarker: PropTypes.func.isRequired,
  TimePickerMarker: PropTypes.func.isRequired,
  EmailFormMarker: PropTypes.func.isRequired,
  isUngatedForAddGuests: PropTypes.bool.isRequired
};
export default connect(state => ({
  isSalesStarter: state.isSalesStarter,
  isRescheduling: state.isRescheduling,
  meetingChangeId: state.meetingChangeId,
  isPaidMeeting: getPaidMeetingsAvailableSelector(state),
  paidMeetingInfo: getPaymentLinkInfo(state),
  recaptchaEnabled: getRecaptchaEnabled(state),
  isUngatedForAddGuests: getIsUngatedForAddGuests(state)
}), FormActions)(CompactBookingPage);